import {
  DropDownItem,
  DropDownMenu,
  useTheme,
} from "@monolith-forensics/monolith-ui";
import {
  EVIDENCE_PRIORITY_ICONS,
  EvidencePriority,
  GetEvidencePriorityResponse,
} from "../evidence-priority.types";
import { useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { EvidencePriorityQuery } from "../evidence-priority.query";
import { EvidencePriorityAPI } from "../evidence-priority.api";
import { Check } from "lucide-react";
import { usePermissions } from "@/hooks";

const resolveIcon = (priority?: EvidencePriority) => {
  if (!priority) return null;
  const Icon = EVIDENCE_PRIORITY_ICONS[priority.icon];
  if (Icon) {
    return <Icon size={14} color={priority.color} />;
  }
  return null;
};

export const EvidencePriorityBtnSelector: React.FC<{
  defaultValue?: number;
  value?: number;
  onChange?: (value: number | null, data: EvidencePriority | null) => void;
}> = ({ defaultValue, value, onChange }) => {
  const theme = useTheme();
  const { hasPermission, MONOLITH_PERMISSIONS } = usePermissions();
  const isDisabled = !hasPermission(MONOLITH_PERMISSIONS.EVIDENCE_UPDATE);
  const isControlled = useRef(value !== undefined);
  const [stateValue, setStateValue] = useState<number | undefined>(
    defaultValue
  );
  const _value = isControlled.current ? value : stateValue;

  const { data } = useQuery<GetEvidencePriorityResponse>({
    queryKey: EvidencePriorityQuery.queryKeys.list(),
    queryFn: () => EvidencePriorityAPI.get(),
  });

  const handleChange = (value: DropDownItem) => {
    if (value.value === "") {
      setStateValue(undefined);
      onChange?.(null, value.data as EvidencePriority | null);
      return;
    }
    setStateValue(value.value as number);
    onChange?.(value.value as number, value.data as EvidencePriority);
  };

  const dropdownOptions: DropDownItem[] = [
    ...(data?.data?.map((priority) => {
      const priorityIdString = priority?.priority_id;
      const isSelected = _value === priorityIdString;

      return {
        value: priority.priority_id,
        label: priority.priority_name,
        data: priority,
        rightSection: isSelected ? <Check size={14} /> : null,
        leftSection: resolveIcon(priority),
      };
    }) ?? []),
  ];

  const selectedPriority = dropdownOptions?.find(
    (option) => option.value === _value
  );

  return (
    <DropDownMenu
      data={dropdownOptions}
      variant="contained"
      disabled={isDisabled}
      size="xs"
      onItemSelect={handleChange}
      buttonProps={{
        size: "xs",
        leftSection: resolveIcon(selectedPriority?.data),
        style: {
          backgroundColor: theme.name === "DARK" ? "#303030" : "#f0f0f0",
          color: theme.name === "DARK" ? theme.palette.text.primary : "#000000",
          opacity: selectedPriority ? 1 : 0.75,
          fontWeight: "normal",
          padding: "2px 5px",
          height: "24px",
          textDecoration: "none",
        },
      }}
      dropDownProps={{
        style: {
          width: 160,
        },
      }}
    >
      {selectedPriority
        ? selectedPriority?.data?.priority_name
        : "Select Priority"}
    </DropDownMenu>
  );
};
